import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { 
  Settings, 
  RefreshCw, 
  Check, 
  X, 
  Phone, 
  Mail, 
  CreditCard, 
  Brain, 
  BarChart2,
  Tags,
  Play,
  AlertCircle,
  ExternalLink,
  Save,
  Eye,
  EyeOff,
  FileText,
  Edit,
  RotateCcw,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  'phone': Phone,
  'mail': Mail,
  'credit-card': CreditCard,
  'brain': Brain,
  'bar-chart': BarChart2,
  'tags': Tags,
};

export default function AdminIntegrationsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [testingIntegration, setTestingIntegration] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [summary, setSummary] = useState({ total: 0, configured: 0, not_configured: 0 });
  const [trackingTags, setTrackingTags] = useState({
    google_analytics: { id: '', configured: false },
    google_tag_manager: { id: '', configured: false },
    facebook_pixel: { id: '', configured: false },
    hotjar: { id: '', configured: false },
  });
  const [trackingForm, setTrackingForm] = useState({
    google_analytics_id: '',
    gtm_container_id: '',
    fb_pixel_id: '',
    hotjar_id: '',
  });
  const [showKeys, setShowKeys] = useState({});
  
  // Email Templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Notification Settings state
  const [notifSettings, setNotifSettings] = useState({
    cc_email: 'info@readysetstem.org',
    from_email: 'info@readysetstem.org',
    from_name: 'Ready Set STEM',
    sms_notify_number: '',
    sms_notify_enabled: false,
  });
  const [savingNotif, setSavingNotif] = useState(false);

  useEffect(() => {
    fetchIntegrations();
    fetchTrackingTags();
    fetchEmailTemplates();
    fetchNotificationSettings();
  }, []);

  const fetchIntegrations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/integrations/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setIntegrations(data.integrations || []);
        setSummary(data.summary || { total: 0, configured: 0, not_configured: 0 });
      }
    } catch (error) {
      console.error('Error fetching integrations:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrackingTags = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/integrations/tracking/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTrackingTags(data);
        setTrackingForm({
          google_analytics_id: data.google_analytics?.id || '',
          gtm_container_id: data.google_tag_manager?.id || '',
          fb_pixel_id: data.facebook_pixel?.id || '',
          hotjar_id: data.hotjar?.id || '',
        });
      }
    } catch (error) {
      console.error('Error fetching tracking tags:', error);
    }
  };

  const testIntegration = async (integrationName) => {
    let testEmail = '';
    if (integrationName.toLowerCase() === 'sendgrid') {
      testEmail = window.prompt('Enter email address to send test email to:');
      if (!testEmail) return;
    }
    setTestingIntegration(integrationName);
    try {
      const response = await fetch(`${API_URL}/api/admin/integrations/test/${integrationName.toLowerCase()}/`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ test_email: testEmail }),
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success(`${integrationName}: ${data.message}`);
      } else {
        toast.error(`${integrationName}: ${data.message}`);
      }
    } catch (error) {
      toast.error(`Failed to test ${integrationName}`);
    } finally {
      setTestingIntegration(null);
    }
  };

  const saveTrackingTags = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/integrations/tracking/save/`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(trackingForm),
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Tracking tags saved successfully');
        fetchTrackingTags();
      } else {
        toast.error('Failed to save tracking tags');
      }
    } catch (error) {
      toast.error('Failed to save tracking tags');
    }
  };

  // Notification Settings functions
  const fetchNotificationSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/integrations/notifications/`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setNotifSettings(data);
      }
    } catch {}
  };

  const saveNotificationSettings = async () => {
    setSavingNotif(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/integrations/notifications/save/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(notifSettings),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Notification settings saved');
      } else {
        toast.error('Failed to save settings');
      }
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSavingNotif(false);
    }
  };

  // Email Templates functions
  const fetchEmailTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setEmailTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching email templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchTemplateDetail = async (templateType) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/${templateType}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedTemplate(data);
        setEditingTemplate(null);
        previewTemplate(templateType);
      }
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const previewTemplate = async (templateType) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/${templateType}/preview/`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        setTemplatePreview(data);
      }
    } catch (error) {
      console.error('Error previewing template:', error);
    }
  };

  const saveTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/${selectedTemplate.template_type}/update/`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subject: editingTemplate.subject,
          html_content: editingTemplate.html_content,
        }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Template saved successfully');
        setEditingTemplate(null);
        fetchTemplateDetail(selectedTemplate.template_type);
      } else {
        toast.error('Failed to save template');
      }
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const resetTemplate = async (templateType) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-templates/${templateType}/reset/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Template reset to default');
        fetchTemplateDetail(templateType);
      }
    } catch (error) {
      toast.error('Failed to reset template');
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || Settings;
    return IconComponent;
  };

  return (
    <div className="space-y-6" data-testid="admin-integrations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <Settings className="h-7 w-7 text-primary" />
            API Integrations
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage third-party service integrations and tracking tags
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchIntegrations}
          data-testid="refresh-integrations-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Settings className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Integrations</p>
              <h3 className="text-2xl font-bold">{summary.total}</h3>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Configured</p>
              <h3 className="text-2xl font-bold text-green-600">{summary.configured}</h3>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Not Configured</p>
              <h3 className="text-2xl font-bold text-amber-600">{summary.not_configured}</h3>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="services">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="services" data-testid="tab-services">Integrations</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">Notifications</TabsTrigger>
          <TabsTrigger value="tracking" data-testid="tab-tracking">Tracking Tags</TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails">Email Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="services" className="space-y-4">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-6 bg-muted rounded w-24 mb-4"></div>
                    <div className="h-4 bg-muted rounded w-full mb-2"></div>
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {integrations.map((integration) => {
                const Icon = getIcon(integration.icon);
                return (
                  <Card 
                    key={integration.name} 
                    className={integration.configured ? 'border-green-200' : 'border-amber-200'}
                    data-testid={`integration-${integration.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                            integration.configured ? 'bg-green-100' : 'bg-amber-100'
                          }`}>
                            <Icon className={`h-5 w-5 ${
                              integration.configured ? 'text-green-600' : 'text-amber-600'
                            }`} />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{integration.name}</CardTitle>
                            <CardDescription>{integration.description}</CardDescription>
                          </div>
                        </div>
                        <Badge variant={integration.configured ? 'default' : 'secondary'}>
                          {integration.configured ? 'Active' : 'Not Configured'}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Features */}
                      <div className="mb-4">
                        <p className="text-xs text-muted-foreground mb-2">Features:</p>
                        <div className="flex flex-wrap gap-1">
                          {integration.features?.map((feature, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* API Keys Status */}
                      <div className="mb-4">
                        <p className="text-xs text-muted-foreground mb-2">Required Keys:</p>
                        <div className="space-y-1">
                          {integration.keys && Object.entries(integration.keys).map(([key, isSet]) => (
                            <div key={key} className="flex items-center gap-2 text-sm">
                              {isSet ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <X className="h-4 w-4 text-red-500" />
                              )}
                              <code className="text-xs bg-muted px-1 rounded">{key}</code>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        {integration.configured && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => testIntegration(integration.name)}
                            disabled={testingIntegration === integration.name}
                            data-testid={`test-${integration.name.toLowerCase()}-btn`}
                          >
                            {testingIntegration === integration.name ? (
                              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                            ) : (
                              <Play className="h-4 w-4 mr-1" />
                            )}
                            Test
                          </Button>
                        )}
                        {integration.docs_url && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(integration.docs_url, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Docs
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Setup Instructions */}
          <Card>
            <CardHeader>
              <CardTitle>Configuration Instructions</CardTitle>
              <CardDescription>How to set up API integrations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-sm">
                <div>
                  <h4 className="font-semibold mb-1">1. Obtain API Keys</h4>
                  <p className="text-muted-foreground">
                    Visit each service's developer portal to create API keys. Click the "Docs" button on each integration card for direct links.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">2. Add to Environment</h4>
                  <p className="text-muted-foreground">
                    Add your API keys to the <code className="bg-muted px-1 rounded">.env</code> file in the backend directory. The required key names are shown above.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">3. Restart Server</h4>
                  <p className="text-muted-foreground">
                    After updating environment variables, restart the backend server for changes to take effect.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">4. Test Integration</h4>
                  <p className="text-muted-foreground">
                    Use the "Test" button to verify each integration is working correctly.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Email Notification Settings</CardTitle>
              <CardDescription>Configure the email address that receives enrollment confirmations and the sender details for outgoing emails.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-semibold">CC Email for Enrollment Confirmations</Label>
                  <Input
                    value={notifSettings.cc_email}
                    onChange={e => setNotifSettings(p => ({...p, cc_email: e.target.value}))}
                    placeholder="info@readysetstem.org"
                    data-testid="notif-cc-email"
                  />
                  <p className="text-xs text-muted-foreground">This email will be CC'd on every enrollment confirmation sent to parents.</p>
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">From Email (Sender)</Label>
                  <Input
                    value={notifSettings.from_email}
                    onChange={e => setNotifSettings(p => ({...p, from_email: e.target.value}))}
                    placeholder="info@readysetstem.org"
                    data-testid="notif-from-email"
                  />
                  <p className="text-xs text-muted-foreground">The email address that appears as the sender in outgoing emails.</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold">From Name (Sender Name)</Label>
                <Input
                  value={notifSettings.from_name}
                  onChange={e => setNotifSettings(p => ({...p, from_name: e.target.value}))}
                  placeholder="Ready Set STEM"
                  className="max-w-md"
                  data-testid="notif-from-name"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Phone className="h-5 w-5" /> SMS Notification Settings</CardTitle>
              <CardDescription>Get a text message to your phone every time a parent enrolls their child. Uses Twilio to deliver.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={notifSettings.sms_notify_enabled}
                    onChange={e => setNotifSettings(p => ({...p, sms_notify_enabled: e.target.checked}))}
                    className="rounded"
                    id="sms-enabled"
                    data-testid="notif-sms-enabled"
                  />
                  <Label htmlFor="sms-enabled" className="font-semibold cursor-pointer">Enable SMS notifications for new enrollments</Label>
                </div>
              </div>
              {notifSettings.sms_notify_enabled && (
                <div className="space-y-2 max-w-md">
                  <Label className="font-semibold">Admin Phone Number</Label>
                  <Input
                    type="tel"
                    value={notifSettings.sms_notify_number}
                    onChange={e => setNotifSettings(p => ({...p, sms_notify_number: e.target.value}))}
                    placeholder="(555) 123-4567"
                    data-testid="notif-sms-number"
                  />
                  <p className="text-xs text-muted-foreground">You'll receive a text like: "New enrollment! Emma Smith registered for Junior Doctors at Ready Set STEM - Naperville."</p>
                </div>
              )}
              <Button onClick={saveNotificationSettings} disabled={savingNotif} data-testid="save-notification-settings">
                {savingNotif ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Notification Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tracking" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Analytics & Tracking Tags</CardTitle>
              <CardDescription>
                Configure website analytics and marketing tracking pixels
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Google Analytics */}
              <div className="space-y-2">
                <Label htmlFor="ga-id" className="flex items-center gap-2">
                  <BarChart2 className="h-4 w-4" />
                  Google Analytics ID
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="ga-id"
                    placeholder="G-XXXXXXXXXX"
                    value={trackingForm.google_analytics_id}
                    onChange={(e) => setTrackingForm(prev => ({...prev, google_analytics_id: e.target.value}))}
                    data-testid="ga-id-input"
                  />
                  {trackingTags.google_analytics?.configured && (
                    <Badge variant="default" className="whitespace-nowrap">Configured</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Found in Google Analytics under Admin → Property → Data Streams
                </p>
              </div>

              {/* Google Tag Manager */}
              <div className="space-y-2">
                <Label htmlFor="gtm-id" className="flex items-center gap-2">
                  <Tags className="h-4 w-4" />
                  Google Tag Manager Container ID
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="gtm-id"
                    placeholder="GTM-XXXXXXX"
                    value={trackingForm.gtm_container_id}
                    onChange={(e) => setTrackingForm(prev => ({...prev, gtm_container_id: e.target.value}))}
                    data-testid="gtm-id-input"
                  />
                  {trackingTags.google_tag_manager?.configured && (
                    <Badge variant="default" className="whitespace-nowrap">Configured</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Found in GTM dashboard under Container ID
                </p>
              </div>

              {/* Facebook Pixel */}
              <div className="space-y-2">
                <Label htmlFor="fb-pixel" className="flex items-center gap-2">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook Pixel ID
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="fb-pixel"
                    placeholder="123456789012345"
                    value={trackingForm.fb_pixel_id}
                    onChange={(e) => setTrackingForm(prev => ({...prev, fb_pixel_id: e.target.value}))}
                    data-testid="fb-pixel-input"
                  />
                  {trackingTags.facebook_pixel?.configured && (
                    <Badge variant="default" className="whitespace-nowrap">Configured</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Found in Meta Business Suite under Events Manager → Data Sources
                </p>
              </div>

              {/* Hotjar */}
              <div className="space-y-2">
                <Label htmlFor="hotjar-id" className="flex items-center gap-2">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22c-5.514 0-10-4.486-10-10S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10zm-1-15h2v6h-2zm0 8h2v2h-2z"/>
                  </svg>
                  Hotjar Site ID
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="hotjar-id"
                    placeholder="1234567"
                    value={trackingForm.hotjar_id}
                    onChange={(e) => setTrackingForm(prev => ({...prev, hotjar_id: e.target.value}))}
                    data-testid="hotjar-id-input"
                  />
                  {trackingTags.hotjar?.configured && (
                    <Badge variant="default" className="whitespace-nowrap">Configured</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Found in Hotjar dashboard under Sites & Organizations
                </p>
              </div>

              <Button onClick={saveTrackingTags} data-testid="save-tracking-btn">
                <Save className="h-4 w-4 mr-2" />
                Save Tracking Tags
              </Button>
            </CardContent>
          </Card>

          {/* Implementation Guide */}
          <Card>
            <CardHeader>
              <CardTitle>Implementation Guide</CardTitle>
              <CardDescription>How to add tracking tags to your website</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-sm">
                <div>
                  <h4 className="font-semibold mb-1">Google Analytics 4</h4>
                  <p className="text-muted-foreground mb-2">
                    Add this script to your <code className="bg-muted px-1 rounded">index.html</code> head section:
                  </p>
                  <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">
{`<!-- Google Analytics -->
<script async src="https://www.googletagmanager.com/gtag/js?id=YOUR_GA_ID"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'YOUR_GA_ID');
</script>`}
                  </pre>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Google Tag Manager</h4>
                  <p className="text-muted-foreground mb-2">
                    Add this code as high in the <code className="bg-muted px-1 rounded">&lt;head&gt;</code> as possible:
                  </p>
                  <pre className="bg-muted p-3 rounded text-xs overflow-x-auto">
{`<!-- Google Tag Manager -->
<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','YOUR_GTM_ID');</script>`}
                  </pre>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="emails" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Template List */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Email Templates
                </CardTitle>
                <CardDescription>
                  {emailTemplates.length} templates available
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingTemplates ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-12 bg-muted rounded animate-pulse"></div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {emailTemplates.map((template) => (
                      <button
                        key={template.template_type}
                        onClick={() => fetchTemplateDetail(template.template_type)}
                        className={`w-full text-left p-3 rounded-lg border transition-colors ${
                          selectedTemplate?.template_type === template.template_type
                            ? 'bg-primary/10 border-primary'
                            : 'hover:bg-muted border-transparent'
                        }`}
                        data-testid={`template-${template.template_type}`}
                      >
                        <div className="font-medium text-sm">{template.name}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {template.subject}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Template Preview/Edit */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>
                      {selectedTemplate ? selectedTemplate.name : 'Select a Template'}
                    </CardTitle>
                    <CardDescription>
                      {selectedTemplate ? 'Preview and customize email content' : 'Choose a template from the list'}
                    </CardDescription>
                  </div>
                  {selectedTemplate && (
                    <div className="flex gap-2">
                      {editingTemplate ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setEditingTemplate(null)}>
                            Cancel
                          </Button>
                          <Button size="sm" onClick={saveTemplate}>
                            <Save className="h-4 w-4 mr-1" />
                            Save
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => setEditingTemplate({
                              subject: selectedTemplate.subject,
                              html_content: selectedTemplate.html_content
                            })}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => resetTemplate(selectedTemplate.template_type)}
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Reset
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {selectedTemplate ? (
                  <div className="space-y-4">
                    {/* Subject Line */}
                    <div>
                      <Label className="text-sm font-medium">Subject Line</Label>
                      {editingTemplate ? (
                        <Input
                          value={editingTemplate.subject}
                          onChange={(e) => setEditingTemplate(prev => ({...prev, subject: e.target.value}))}
                          className="mt-1"
                        />
                      ) : (
                        <div className="mt-1 p-2 bg-muted rounded text-sm">
                          {templatePreview?.subject || selectedTemplate.subject}
                        </div>
                      )}
                    </div>

                    {/* Available Variables */}
                    <div>
                      <Label className="text-sm font-medium">Available Variables</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedTemplate.available_variables?.map((variable) => (
                          <Badge key={variable} variant="secondary" className="text-xs font-mono">
                            {`{{${variable}}}`}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Preview or Edit */}
                    {editingTemplate ? (
                      <div>
                        <Label className="text-sm font-medium">HTML Content</Label>
                        <Textarea
                          value={editingTemplate.html_content}
                          onChange={(e) => setEditingTemplate(prev => ({...prev, html_content: e.target.value}))}
                          className="mt-1 font-mono text-xs h-[400px]"
                        />
                      </div>
                    ) : (
                      <div>
                        <Label className="text-sm font-medium">Email Preview</Label>
                        <div className="mt-1 border rounded-lg overflow-hidden bg-white">
                          <iframe
                            srcDoc={templatePreview?.html_content || ''}
                            className="w-full h-[400px]"
                            title="Email Preview"
                            sandbox="allow-same-origin"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Select a template from the list to preview</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Template Guide */}
          <Card>
            <CardHeader>
              <CardTitle>Using Email Templates</CardTitle>
              <CardDescription>How templates work with SendGrid</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <span className="h-6 w-6 rounded-full bg-primary text-white flex items-center justify-center text-xs">1</span>
                    Customize Templates
                  </h4>
                  <p className="text-muted-foreground">
                    Edit the subject line and HTML content to match your brand. Use variables like <code className="bg-muted px-1 rounded">{`{{learner_name}}`}</code> for dynamic content.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <span className="h-6 w-6 rounded-full bg-primary text-white flex items-center justify-center text-xs">2</span>
                    Connect SendGrid
                  </h4>
                  <p className="text-muted-foreground">
                    Add your SendGrid API key in the Service Integrations tab. Templates will automatically be used when sending emails.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <span className="h-6 w-6 rounded-full bg-primary text-white flex items-center justify-center text-xs">3</span>
                    Send Emails
                  </h4>
                  <p className="text-muted-foreground">
                    Emails are sent automatically for enrollment confirmations, password resets, class reminders, and more.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
